import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Radio } from "lucide-react";
import { useViewerAuth } from "@/context/ViewerAuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { YSVCHI_SIGN_UP_URL } from "@/lib/ysvAuthApi";

export default function SignIn() {
  const { user, loading, login } = useViewerAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!loading && user) navigate("/games", { replace: true });
  }, [loading, user, navigate]);

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email, password);
      navigate("/games", { replace: true });
    } catch (err) {
      setError(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not sign in. Please try again.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#0A0A0A] px-6 py-12 text-white">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(0,122,255,0.18),transparent_45%)]" />
      <div className="relative w-full max-w-md">
        <div className="mb-8 text-center">
          <span className="mx-auto flex h-14 w-14 items-center justify-center bg-[#007AFF]">
            <Radio className="h-7 w-7" />
          </span>
          <h1 className="mt-5 font-display text-4xl font-black uppercase tracking-tighter">
            YSV<span className="text-[#007AFF]">Live</span>
          </h1>
          <p className="mt-2 text-white/50">Sign in with your YSVCHI account.</p>
        </div>

        <div className="border border-white/10 bg-[#141414] p-7 md:p-8">
          <form onSubmit={submit} className="space-y-5">
            <div>
              <label className="mb-1.5 block font-display text-xs font-bold uppercase tracking-[0.2em] text-white/60">
                Email
              </label>
              <input
                data-testid="viewer-email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
                className="w-full border border-white/10 bg-[#0A0A0A] px-4 py-3 text-white focus:border-[#007AFF] focus:outline-none focus:ring-1 focus:ring-[#007AFF]"
              />
            </div>
            <div>
              <label className="mb-1.5 block font-display text-xs font-bold uppercase tracking-[0.2em] text-white/60">
                Password
              </label>
              <input
                data-testid="viewer-password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
                className="w-full border border-white/10 bg-[#0A0A0A] px-4 py-3 text-white focus:border-[#007AFF] focus:outline-none focus:ring-1 focus:ring-[#007AFF]"
              />
            </div>
            {error && (
              <p data-testid="viewer-login-error" className="text-sm text-[#ff6b6b]">
                {error}
              </p>
            )}
            <button
              type="submit"
              data-testid="viewer-sign-in"
              disabled={busy}
              className="flex w-full items-center justify-center gap-2 bg-[#007AFF] py-3.5 font-display text-sm font-bold uppercase tracking-widest transition-colors hover:bg-[#0062cc] disabled:opacity-50"
            >
              {busy ? "Signing In…" : "Sign In"}
              {!busy && <ArrowRight className="h-4 w-4" />}
            </button>
          </form>

          <div className="my-6 flex items-center gap-3 text-white/25">
            <span className="h-px flex-1 bg-white/10" />
            <span className="font-display text-[10px] font-bold uppercase tracking-[0.2em]">
              New to YSV?
            </span>
            <span className="h-px flex-1 bg-white/10" />
          </div>

          <a
            href={YSVCHI_SIGN_UP_URL}
            data-testid="sign-up-on-ysvchi"
            className="block w-full border border-white/20 px-5 py-3.5 text-center font-display text-sm font-bold uppercase tracking-widest text-white transition-colors hover:border-[#007AFF] hover:text-[#007AFF]"
          >
            Sign Up on YSVCHI
          </a>
          <p className="mt-4 text-center text-xs leading-relaxed text-white/35">
            Account creation and payments are handled securely on ysvchi.com.
          </p>
        </div>
      </div>
    </main>
  );
}
